import {
    Table, Button, Modal, Input, Tag, message, Row, Col, Typography,
    Select, DatePicker, Form, Divider, Spin, Tooltip
} from "antd";
import {
    PlusOutlined, CheckOutlined, CloseOutlined, EyeOutlined,
    SearchOutlined, SwapOutlined, EditOutlined, DeleteOutlined,
    InfoCircleOutlined, DownloadOutlined
} from "@ant-design/icons";
import { useState, useContext, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import axios from "axios";
import * as XLSX from "xlsx";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";

const { Title } = Typography;
const STATUS_COLORS = { PENDING: "orange", APPROVED: "green", REJECTED: "red" };

export default function EmpTransfer() {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);
    const headers = { Authorization: `Bearer ${authData?.access}` };
    const isSuperuser = authData?.employee?.is_superuser;
    const isHR = !isSuperuser;

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [counts, setCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0 });
    const [statusFilter, setStatusFilter] = useState("PENDING");
    const [searchText, setSearchText] = useState("");

    // form modal
    const [formVisible, setFormVisible] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [empLoading, setEmpLoading] = useState(false);

    // view modal
    const [viewRecord, setViewRecord] = useState(null);

    // approve / reject modals
    const [approveRecord, setApproveRecord] = useState(null);
    const [approveRemarks, setApproveRemarks] = useState("");
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectRecord, setRejectRecord] = useState(null);
    const [rejectRemarks, setRejectRemarks] = useState("");
    const [rejectLoading, setRejectLoading] = useState(false);

    // delete
    const [deleteId, setDeleteId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // mail
    const [mailLoadingId, setMailLoadingId] = useState(null);

    // master data
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [organisations, setOrganisations] = useState([]);
    const [masterLoading, setMasterLoading] = useState(true);

    useEffect(() => {
        const fetchMaster = async () => {
            setMasterLoading(true);
            try {
                const [e, d, des, o] = await Promise.all([
                    axios.get(`${backUrl}/api/emp/employees/mini_list/`, { headers }),
                    axios.get(`${backUrl}/api/emp/departments/`, { headers }),
                    axios.get(`${backUrl}/api/emp/designations/`, { headers }),
                    axios.get(`${backUrl}/api/emp/organisations/`, { headers }),
                ]);
                setEmployees(e.data?.results || e.data || []);
                setDepartments(d.data?.results || d.data || []);
                setDesignations(des.data?.results || des.data || []);
                setOrganisations(o.data?.results || o.data || []);
            } catch { /* silent */ }
            finally { setMasterLoading(false); }
        };
        fetchMaster();
    }, []);

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams();
            if (statusFilter) p.append("status", statusFilter);
            if (searchText.trim()) p.append("search", searchText.trim());
            const res = await axios.get(`${backUrl}/api/emp/transfer-orders/?${p}`, { headers });
            setRecords(res.data?.results || res.data || []);
        } catch { message.error("Failed to load transfer orders."); }
        finally { setLoading(false); }
    }, [backUrl, authData, statusFilter, searchText]);

    const fetchCounts = useCallback(async () => {
        try {
            const results = await Promise.all(
                ["PENDING","APPROVED","REJECTED"].map(s =>
                    axios.get(`${backUrl}/api/emp/transfer-orders/?status=${s}&page_size=1`, { headers })
                        .then(r => [s, r.data?.count ?? (Array.isArray(r.data) ? r.data.length : 0)])
                        .catch(() => [s, 0])
                )
            );
            setCounts(Object.fromEntries(results));
        } catch { /* silent */ }
    }, [backUrl, authData]);

    // ✅ Sync with NavigationContext
    useEffect(() => {
        if (navigationParams?.status) {
            console.log("🎯 EmpTransfer: Syncing status from navigation:", navigationParams.status);
            setStatusFilter(navigationParams.status.toUpperCase());
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // ✅ Fetch records when status or search changes
    useEffect(() => {
        // Only fetch if we are not in the middle of a navigation sync
        // Or just let it fetch, but since statusFilter is a dependency, 
        // it will fetch correctly when the state updates.
        fetchRecords();
        fetchCounts();
    }, [fetchRecords, fetchCounts, statusFilter]);

    const selectedOrg = Form.useWatch("new_organisation", form);
    const selectedDept = Form.useWatch("new_department", form);

    const empOptions = employees.map(e => ({
        value: e.id,
        label: `${e.first_name||""} ${e.last_name||""}`.trim() + (e.emp_code ? ` (${e.emp_code})` : ""),
        department_name: e.department_name,
        department: e.department,   // keep the FK id for robust matching
        is_hod: e.is_hod,
        is_superuser: e.is_superuser,
        reporting_hod: e.reporting_hod
    }));
    const deptOptions = departments.map(d => ({ 
        value: d.id, 
        label: d.name || d.department_name,
        organisation: d.organisation || d.organisation_id
    }));
    const desigOptions = designations.map(d => ({ 
        value: d.id, 
        label: d.name || d.designation_name,
        department: d.department || d.department_id
    }));
    const orgOptions = organisations.map(o => ({ value: o.id, label: o.name || o.organisation_name }));

    const filteredDeptOptions = selectedOrg ? deptOptions.filter(d => d.organisation === selectedOrg) : deptOptions;
    const filteredDesigOptions = selectedDept ? desigOptions.filter(d => d.department === selectedDept) : desigOptions;

    // ── Build "New Reporting To" options ──────────────────────────────────────
    // Match by department FK id (robust), not department_name string
    const empsInSelectedDept = selectedDept
        ? empOptions.filter(e => e.department === selectedDept)
        : [];

    // Always include all HODs and superusers so the user can pick a direct-HOD
    // reporting line when there is no TL in the target department.
    const hodAndSuperuserOptions = empOptions.filter(e => e.is_hod || e.is_superuser);

    let filteredReportingOptions;
    if (selectedDept) {
        // Merge dept employees + HODs/superusers, deduplicate by value (id)
        const deptSet = new Set(empsInSelectedDept.map(e => e.value));
        const extra   = hodAndSuperuserOptions.filter(e => !deptSet.has(e.value));
        filteredReportingOptions = [...empsInSelectedDept, ...extra];
    } else {
        filteredReportingOptions = empOptions;
    }

    // HOD dropdown: all HODs and superusers in the company (cross-department)
    const filteredHODOptions = empOptions.filter(e => e.is_hod || e.is_superuser);

    // ── Helper: derive HOD from a selected manager (mirrors backend _derive_hod) ─
    const deriveHODFromManager = (managerId) => {
        if (!managerId) return null;
        // BFS/traversal up the chain using the loaded employees array
        const empMap = Object.fromEntries(employees.map(e => [e.id, e]));
        let current = empMap[managerId];
        const visited = new Set();
        while (current) {
            if (visited.has(current.id)) break;
            visited.add(current.id);
            if (current.is_hod || current.is_superuser) return current.id;
            // Walk up via reporting_hod (the already-resolved HOD stored on the employee)
            if (current.reporting_hod) return current.reporting_hod;
            break; // no further upward info in mini_list
        }
        return null;
    };

    const handleOrgChange = () => {
        form.setFieldsValue({ new_department: null, new_designation: null, new_reporting_to: null, new_reporting_hod: null });
    };

    const handleDeptChange = () => {
        form.setFieldsValue({ new_designation: null, new_reporting_to: null, new_reporting_hod: null });
    };

    // Auto-derive HOD when Reporting To is selected
    const handleReportingToChange = (managerId) => {
        if (!managerId) {
            // If cleared, also clear HOD so user can pick manually if needed
            form.setFieldsValue({ new_reporting_hod: null });
            return;
        }
        const derivedHodId = deriveHODFromManager(managerId);
        if (derivedHodId) {
            form.setFieldsValue({ new_reporting_hod: derivedHodId });
        } else {
            // Could not auto-derive — leave HOD blank for manual selection
            form.setFieldsValue({ new_reporting_hod: null });
        }
    };

    const generateDefaultEmailTemplate = (recordOrValues = {}) => {
        const empId = recordOrValues.employee || form.getFieldValue("employee");
        const selectedEmpObj = employees.find(e => e.id === empId);
        const empName = recordOrValues.employee_name || (selectedEmpObj ? `${selectedEmpObj.first_name || ''} ${selectedEmpObj.last_name || ''}`.trim() : "Employee");

        const effDateVal = recordOrValues.effective_date_of_transfer || form.getFieldValue("effective_date_of_transfer");
        const effDateStr = effDateVal ? dayjs(effDateVal).format("DD MMMM YYYY") : "[Effective Date]";

        const currOrgId = recordOrValues.current_organisation || form.getFieldValue("current_organisation");
        const newOrgId = recordOrValues.new_organisation || form.getFieldValue("new_organisation");
        const currOrgObj = organisations.find(o => o.id === currOrgId);
        const newOrgObj = organisations.find(o => o.id === newOrgId);
        const currOrgName = currOrgObj?.name || currOrgObj?.organisation_name || "Baashyaam Group";
        const newOrgName = newOrgObj?.name || newOrgObj?.organisation_name || currOrgName;

        let roleStatement = "";
        if (currOrgId && newOrgId && currOrgId !== newOrgId) {
            roleStatement = `This is to inform you that you have been transferred from ${currOrgName} to ${newOrgName}, effective ${effDateStr}.`;
        } else {
            roleStatement = `This is to inform you that there is a change in your role and/or department within the organization, effective ${effDateStr}.`;
        }

        const currDeptId = recordOrValues.current_department || form.getFieldValue("current_department");
        const newDeptId = recordOrValues.new_department || form.getFieldValue("new_department");
        const currDeptObj = departments.find(d => d.id === currDeptId);
        const newDeptObj = departments.find(d => d.id === newDeptId);
        const currDeptName = currDeptObj?.name || currDeptObj?.department_name || "Current Department";
        const newDeptName = newDeptObj?.name || newDeptObj?.department_name || currDeptName;

        let deptStatement = "";
        if (newDeptId && currDeptId !== newDeptId) {
            deptStatement = `Your department has been changed from ${currDeptName} to ${newDeptName}.`;
        } else {
            deptStatement = `Your department remains unchanged (${currDeptName}).`;
        }

        const currDesigId = recordOrValues.current_designation || form.getFieldValue("current_designation");
        const newDesigId = recordOrValues.new_designation || form.getFieldValue("new_designation");
        const currDesigObj = designations.find(d => d.id === currDesigId);
        const newDesigObj = designations.find(d => d.id === newDesigId);
        const currDesigName = currDesigObj?.name || currDesigObj?.designation_name || "Current Designation";
        const newDesigName = newDesigObj?.name || newDesigObj?.designation_name || currDesigName;

        let desigStatement = "";
        if (newDesigId && currDesigId !== newDesigId) {
            desigStatement = `Your designation has been changed from ${currDesigName} to ${newDesigName}.`;
        } else {
            desigStatement = `Your designation remains unchanged (${currDesigName}).`;
        }

        return [
            `Dear ${empName},`,
            roleStatement,
            `Effective Date of Transfer: ${effDateStr}`,
            deptStatement,
            desigStatement,
            "Please note that there will be no change in your current remuneration or other terms and conditions of employment, and they will remain unchanged.",
            "We request you to ensure a smooth transition of your responsibilities.",
            "If you require any clarification, please feel free to contact the HR Department.",
            "We wish you all the best in your new role.",
            `Warm regards,\nHR Department\n${newOrgName}`
        ].join("\n\n");
    };

    const handleEmployeeSelect = async (empId) => {
        if (!empId) return;
        setEmpLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/emp/employees/${empId}/`, { headers });
            const emp = res.data;
            const empName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
            form.setFieldsValue({
                current_employee_code: emp.emp_code || "",
                current_organisation: emp.organisation || null,
                current_department: emp.department || null,
                current_designation: emp.designation || null,
                current_reporting_to: emp.reporting_to || null,
                current_reporting_hod: emp.reporting_hod || null,
                current_location: emp.location || "",
                to_mail: emp.user?.email || "",
                subject: `Transfer Order - ${empName}`,
            });

            if (!form.getFieldValue("email_body")) {
                form.setFieldsValue({
                    email_body: generateDefaultEmailTemplate({ employee: empId, employee_name: empName })
                });
            }
        } catch { message.error("Could not fetch employee details."); }
        finally { setEmpLoading(false); }
    };

    const openForm = (record = null) => {
        setEditRecord(record);
        form.resetFields();
        if (record) {
            form.setFieldsValue({
                employee: record.employee,
                current_employee_code: record.current_employee_code,
                current_organisation: record.current_organisation,
                current_department: record.current_department,
                current_designation: record.current_designation,
                current_reporting_to: record.current_reporting_to,
                current_reporting_hod: record.current_reporting_hod,
                current_location: record.current_location,
                new_employee_code: record.new_employee_code,
                new_organisation: record.new_organisation,
                new_department: record.new_department,
                new_designation: record.new_designation,
                new_reporting_to: record.new_reporting_to,
                new_reporting_hod: record.new_reporting_hod,
                new_location: record.new_location,
                transfer_initiation_date: record.transfer_initiation_date ? dayjs(record.transfer_initiation_date) : null,
                effective_date_of_transfer: record.effective_date_of_transfer ? dayjs(record.effective_date_of_transfer) : null,
                subject: record.subject, from_mail: record.from_mail,
                to_mail: record.to_mail, cc_mails: record.cc_mails, 
                email_body: record.email_body || generateDefaultEmailTemplate(record), 
                remarks: record.remarks,
            });
        } else {
            form.setFieldsValue({ 
                transfer_initiation_date: dayjs(),
                email_body: generateDefaultEmailTemplate()
            });
        }
        setFormVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            // Convert empty strings to null to avoid backend blank=False errors
            const cleanedValues = Object.fromEntries(
                Object.entries(values).map(([k, v]) => [k, v === "" ? null : v])
            );

            const payload = {
                ...cleanedValues,
                transfer_initiation_date: values.transfer_initiation_date?.format("YYYY-MM-DD") || null,
                effective_date_of_transfer: values.effective_date_of_transfer?.format("YYYY-MM-DD") || null,
            };

            if (editRecord) {
                await axios.patch(`${backUrl}/api/emp/transfer-orders/${editRecord.id}/`, payload, { headers });
                message.success("Transfer order updated.");
            } else {
                await axios.post(`${backUrl}/api/emp/transfer-orders/`, payload, { headers });
                message.success("Transfer order created. Pending superuser approval.");
            }
            setFormVisible(false); form.resetFields(); setEditRecord(null);
            fetchRecords(); fetchCounts();
        } catch (err) {
            if (err?.errorFields) return; // Ant Design validation error, already shown inline
            const data = err?.response?.data;
            let msg = "Failed to save.";
            if (data) {
                if (typeof data === "string") {
                    msg = data;
                } else if (data.message) {
                    msg = data.message;
                } else if (data.detail) {
                    msg = data.detail;
                } else {
                    // data is an object like { field: "error" } or { field: ["error"] }
                    const firstVal = Object.values(data)[0];
                    msg = Array.isArray(firstVal) ? firstVal[0] : (firstVal || msg);
                }
            }
            message.error(msg);
        } finally { setSubmitting(false); }
    };

    const handleApprove = async () => {
        setApproveLoading(true);
        try {
            await axios.patch(
                `${backUrl}/api/emp/transfer-orders/${approveRecord.id}/update-status/`,
                { status: "APPROVED", remarks: approveRemarks }, { headers }
            );
            message.success("Transfer approved. Employee record updated.");
            setApproveRecord(null); setApproveRemarks("");
            fetchRecords(); fetchCounts();
        } catch (err) { message.error(err?.response?.data?.error || "Approval failed."); }
        finally { setApproveLoading(false); }
    };

    const handleReject = async () => {
        setRejectLoading(true);
        try {
            await axios.patch(
                `${backUrl}/api/emp/transfer-orders/${rejectRecord.id}/update-status/`,
                { status: "REJECTED", remarks: rejectRemarks }, { headers }
            );
            message.success("Transfer rejected.");
            setRejectRecord(null); setRejectRemarks("");
            fetchRecords(); fetchCounts();
        } catch (err) { message.error(err?.response?.data?.error || "Rejection failed."); }
        finally { setRejectLoading(false); }
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            await axios.delete(`${backUrl}/api/emp/transfer-orders/${deleteId}/`, { headers });
            message.success("Deleted."); setDeleteId(null);
            fetchRecords(); fetchCounts();
        } catch (err) { message.error(err?.response?.data?.error || "Delete failed."); }
        finally { setDeleteLoading(false); }
    };

    const handleSendMail = async (record) => {
        setMailLoadingId(record.id);
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/transfer-orders/${record.id}/send-mail/`, {}, { headers }
            );
            message.success(res.data?.message || "Transfer letter & email sent.");
            fetchRecords();
        } catch (err) { message.error(err?.response?.data?.error || "Mail failed."); }
        finally { setMailLoadingId(null); }
    };

    const columns = [
        {
            title: "Employee",
            key: "emp",
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600, color: "#0e497b" }}>{r.employee_name || "-"}</div>
                    <div 
                        style={{ fontSize: 11, color: "#1890ff", cursor: "pointer" }} 
                        onClick={() => setViewRecord(r)}
                        title="Click to view full details"
                    >
                        {r.current_employee_code}
                    </div>
                </div>
            ),
        },
        {
            title: "From → To Department",
            key: "dept",
            render: (_, r) => (
                <span style={{ fontSize: 12 }}>
                    {r.current_department_name || "-"} <SwapOutlined style={{ color: "#0e497b" }} /> {r.new_department_name || "-"}
                </span>
            ),
        },
        {
            title: "From → To Designation",
            key: "desig",
            render: (_, r) => (
                <span style={{ fontSize: 12 }}>
                    {r.current_designation_name || "-"} <SwapOutlined style={{ color: "#0e497b" }} /> {r.new_designation_name || "-"}
                </span>
            ),
        },
        {
            title: "Effective Date",
            dataIndex: "effective_date_of_transfer",
            render: d => d ? dayjs(d).format("DD/MM/YYYY") : "-",
        },
        {
            title: "Raised By",
            dataIndex: "created_by_name",
            render: v => v || "-",
        },
        {
            title: "Status",
            dataIndex: "status",
            render: t => <Tag color={STATUS_COLORS[t] || "default"}>{t || "-"}</Tag>,
        },
        {
            title: "Emp Response",
            dataIndex: "employee_acceptance_status",
            render: (st, r) => {
                const colors = { ACCEPTED: "green", DECLINED: "red", PENDING: "orange" };
                return (
                    <Tooltip title={r.employee_response_remarks || "No comments"}>
                        <Tag color={colors[st] || "default"}>{st || "PENDING"}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            render: v => v ? (
                <Tooltip title={v}>
                    <span style={{ color: "#856404", cursor: "default", fontSize: 12 }}>
                        {v.length > 30 ? v.slice(0, 30) + "…" : v}
                    </span>
                </Tooltip>
            ) : <span style={{ color: "#ccc", fontSize: 12 }}>—</span>,
        },

        {
            title: "Actions",
            key: "actions",
            render: (_, r) => (
                <div style={{ display: "flex", gap: 6 }}>
                    <Tooltip title="View Details">
                        <Button type="text" icon={<EyeOutlined />} style={{ color: "#0e497b" }}
                            onClick={() => setViewRecord(r)} />
                    </Tooltip>
                    {/* HR can edit/delete their own PENDING orders */}
                    {isHR && r.status === "PENDING" && (
                        <>
                            <Tooltip title="Edit">
                                <Button type="text" icon={<EditOutlined />} style={{ color: "#faad14" }}
                                    onClick={() => openForm(r)} />
                            </Tooltip>
                            <Tooltip title="Delete">
                                <Button type="text" danger icon={<DeleteOutlined />}
                                    onClick={() => setDeleteId(r.id)} />
                            </Tooltip>
                        </>
                    )}
                    {/* Superuser can approve/reject PENDING orders */}
                    {isSuperuser && r.status === "PENDING" && (
                        <>
                            <Tooltip title="Approve">
                                <Button type="text" icon={<CheckOutlined />} style={{ color: "green" }}
                                    onClick={() => setApproveRecord(r)} />
                            </Tooltip>
                            <Tooltip title="Reject">
                                <Button type="text" danger icon={<CloseOutlined />}
                                    onClick={() => setRejectRecord(r)} />
                            </Tooltip>
                        </>
                    )}
                </div>
            ),
        },
    ];

    const handleExportToExcel = () => {
        if (!records || records.length === 0) {
            message.warning("No transfer orders available to export.");
            return;
        }

        const exportRows = records.map((record, index) => {
            return {
                "S. No.": index + 1,
                "Transfer ID": record.id || "-",
                "Employee Name": record.employee_name || "-",
                "Current Emp Code": record.current_employee_code || "-",
                "New Emp Code": record.new_employee_code || record.current_employee_code || "-",
                "Current Organisation": record.current_organisation_name || "-",
                "New Organisation": record.new_organisation_name || "-",
                "Current Department": record.current_department_name || "-",
                "New Department": record.new_department_name || "-",
                "Current Designation": record.current_designation_name || "-",
                "New Designation": record.new_designation_name || "-",
                "Current Reporting Manager": record.current_reporting_to_name || "-",
                "New Reporting Manager": record.new_reporting_to_name || "-",
                "Current HOD": record.current_reporting_hod_name || "-",
                "New HOD": record.new_reporting_hod_name || "-",
                "Current Location": record.current_location || "-",
                "New Location": record.new_location || "-",
                "Initiation Date": record.transfer_initiation_date ? dayjs(record.transfer_initiation_date).format("DD/MM/YYYY") : "-",
                "Effective Date": record.effective_date_of_transfer ? dayjs(record.effective_date_of_transfer).format("DD/MM/YYYY") : "-",
                "Raised By": record.created_by_name || "-",
                "Approval Status": record.status || "-",
                "Employee Response": record.employee_acceptance_status || "PENDING",
                "Employee Comments": record.employee_response_remarks || "-",
                "Approver Remarks": record.remarks || "-",
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(exportRows);

        worksheet["!cols"] = [
            { wch: 8 },  // S. No.
            { wch: 12 }, // Transfer ID
            { wch: 22 }, // Employee Name
            { wch: 16 }, // Current Emp Code
            { wch: 16 }, // New Emp Code
            { wch: 22 }, // Current Organisation
            { wch: 22 }, // New Organisation
            { wch: 22 }, // Current Department
            { wch: 22 }, // New Department
            { wch: 22 }, // Current Designation
            { wch: 22 }, // New Designation
            { wch: 24 }, // Current Manager
            { wch: 24 }, // New Manager
            { wch: 24 }, // Current HOD
            { wch: 24 }, // New HOD
            { wch: 18 }, // Current Location
            { wch: 18 }, // New Location
            { wch: 16 }, // Initiation Date
            { wch: 16 }, // Effective Date
            { wch: 20 }, // Raised By
            { wch: 16 }, // Approval Status
            { wch: 18 }, // Employee Response
            { wch: 28 }, // Employee Comments
            { wch: 28 }, // Approver Remarks
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Transfers");

        const statusLabel = statusFilter ? statusFilter.toLowerCase() : "all";
        const dateStr = dayjs().format("YYYY-MM-DD");
        XLSX.writeFile(workbook, `Employee_Transfers_${statusLabel}_${dateStr}.xlsx`);
        message.success("Exported Employee Transfer records to Excel successfully!");
    };

    return (
        <>
            <Title level={4} style={{ color: "#0e497b", textAlign: "center", textTransform: "uppercase", marginBottom: 20 }}>
                Employee Transfer Orders
            </Title>

            {/* Status Tabs */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
                {[{ key: "PENDING", color: "#faad14" }, { key: "APPROVED", color: "#4CAF50" }, { key: "REJECTED", color: "#f5222d" }]
                    .map(({ key, color }) => (
                        <Button key={key} onClick={() => setStatusFilter(key)}
                            style={{
                                marginRight: 10, minWidth: 130,
                                backgroundColor: statusFilter === key ? color : undefined,
                                borderColor: statusFilter === key ? color : undefined,
                                color: statusFilter === key ? "white" : undefined,
                                fontWeight: statusFilter === key ? 600 : undefined,
                            }}>
                            {key.charAt(0) + key.slice(1).toLowerCase()} ({counts[key] ?? 0})
                        </Button>
                    ))}
            </div>

            {/* Search + Export + Create (HR only) */}
            <div style={{ background: "#fff", padding: 15, borderRadius: 10, marginBottom: 15, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={isHR ? 13 : 18}>
                        <Input
                            placeholder="Search by Employee Name / Code / Department"
                            prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                            value={searchText} onChange={e => setSearchText(e.target.value)} allowClear
                        />
                    </Col>
                    <Col xs={24} sm={6} md={isHR ? 5 : 6}>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={handleExportToExcel}
                            style={{
                                backgroundColor: "#217346",
                                borderColor: "#217346",
                                color: "#ffffff",
                                width: "100%",
                                fontWeight: 600,
                            }}
                        >
                            Export Excel
                        </Button>
                    </Col>
                    {isHR && (
                        <Col xs={24} sm={6} md={6}>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm()}
                                style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", width: "100%" }}>
                                New Transfer Order
                            </Button>
                        </Col>
                    )}
                </Row>
            </div>

            {/* Table */}
            <Table columns={columns} dataSource={records} loading={loading}
                rowKey={r => r.id} scroll={{ x: "max-content" }}
                pagination={{ pageSize: 10 }} bordered
                components={{ header: { cell: props => (
                    <th {...props} style={{ backgroundColor: "#0e497b", color: "white", whiteSpace: "nowrap" }}>
                        {props.children}
                    </th>
                )}}}
            />

            {/* ── Create/Edit Modal (HR only) ── */}
            <Modal title={editRecord ? "Edit Transfer Order" : "New Employee Transfer Order"}
                open={formVisible}
                onCancel={() => { setFormVisible(false); form.resetFields(); setEditRecord(null); }}
                onOk={handleSubmit} okText={editRecord ? "Update" : "Submit"}
                confirmLoading={submitting} width={960}
                okButtonProps={{ style: { backgroundColor: "#0e497b", borderColor: "#0e497b" } }}>
                <Spin spinning={empLoading}>
                    <Form form={form} layout="vertical">

                        {/* Employee Selection */}
                        <Form.Item name="employee" label="Select Employee" rules={[{ required: true, message: "Required" }]}>
                            <Select showSearch placeholder="Search employee by name or code"
                                loading={masterLoading} 
                                notFoundContent={masterLoading ? <div style={{ textAlign: 'center', padding: '10px' }}><Spin size="small" /></div> : null}
                                optionFilterProp="label" options={empOptions} onChange={handleEmployeeSelect} />
                        </Form.Item>

                        <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={12}>
                                <div style={{ background: "#f5f5f5", border: "1px solid #d9d9d9", borderLeft: "4px solid #888", borderRadius: 4, textAlign: "center", padding: "8px 0", fontWeight: 700, color: "#555", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    Current Details (Auto-filled)
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{ background: "#eef4f9", border: "1px solid #d6e4f0", borderLeft: "4px solid #0e497b", borderRadius: 4, textAlign: "center", padding: "8px 0", fontWeight: 700, color: "#0e497b", fontSize: 13, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                    New Transfer Details
                                </div>
                            </Col>
                        </Row>

                        {/* Side-by-side rows */}
                        {[
                            {
                                leftLabel: "Emp Code",
                                leftField: <Form.Item name="current_employee_code" style={{ marginBottom: 0 }}><Input readOnly style={{ background: "#f5f5f5" }} /></Form.Item>,
                                rightLabel: "New Emp Code (Inter-company)",
                                rightField: <Form.Item name="new_employee_code" style={{ marginBottom: 0 }}><Input placeholder="Leave blank if same company" /></Form.Item>,
                            },
                            {
                                leftLabel: "Organisation",
                                leftField: <Form.Item name="current_organisation" style={{ marginBottom: 0 }}><Select options={orgOptions} disabled style={{ width: "100%" }} /></Form.Item>,
                                rightLabel: "New Organisation",
                                rightField: <Form.Item name="new_organisation" style={{ marginBottom: 0 }}><Select showSearch options={orgOptions} optionFilterProp="label" allowClear onChange={handleOrgChange} style={{ width: "100%" }} /></Form.Item>,
                            },
                            {
                                leftLabel: "Department",
                                leftField: <Form.Item name="current_department" style={{ marginBottom: 0 }}><Select options={deptOptions} disabled style={{ width: "100%" }} /></Form.Item>,
                                rightLabel: "New Department *",
                                rightField: <Form.Item name="new_department" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 0 }}><Select showSearch options={filteredDeptOptions} optionFilterProp="label" onChange={handleDeptChange} style={{ width: "100%" }} /></Form.Item>,
                            },
                            {
                                leftLabel: "Designation",
                                leftField: <Form.Item name="current_designation" style={{ marginBottom: 0 }}><Select options={desigOptions} disabled style={{ width: "100%" }} /></Form.Item>,
                                rightLabel: "New Designation *",
                                rightField: <Form.Item name="new_designation" rules={[{ required: true, message: "Required" }]} style={{ marginBottom: 0 }}><Select showSearch options={filteredDesigOptions} optionFilterProp="label" style={{ width: "100%" }} /></Form.Item>,
                            },
                            {
                                leftLabel: "Reporting To (Manager)",
                                leftField: <Form.Item name="current_reporting_to" style={{ marginBottom: 0 }}><Select options={empOptions} disabled style={{ width: "100%" }} /></Form.Item>,
                                rightLabel: (
                                    <span>
                                        New Reporting To
                                        <Tooltip title="HODs and Superusers are always shown here. If there is no TL in the new department, select the HOD directly — the HOD field will auto-fill.">
                                            <InfoCircleOutlined style={{ marginLeft: 5, color: "#1890ff", cursor: "pointer" }} />
                                        </Tooltip>
                                    </span>
                                ),
                                rightField: (
                                    <Form.Item name="new_reporting_to" style={{ marginBottom: 0 }}>
                                        <Select
                                            showSearch
                                            options={filteredReportingOptions}
                                            optionFilterProp="label"
                                            allowClear
                                            onChange={handleReportingToChange}
                                            style={{ width: "100%" }}
                                            placeholder={selectedDept ? "Select manager or HOD" : "Select department first"}
                                        />
                                    </Form.Item>
                                ),
                            },
                            {
                                leftLabel: "HOD",
                                leftField: <Form.Item name="current_reporting_hod" style={{ marginBottom: 0 }}><Select options={empOptions} disabled style={{ width: "100%" }} /></Form.Item>,
                                rightLabel: (
                                    <span>
                                        New HOD
                                        <Tooltip title="Auto-filled when you select a Reporting To. If the employee reports directly to the HOD (no TL), this will also auto-fill when you pick the HOD above. You can override manually.">
                                            <InfoCircleOutlined style={{ marginLeft: 5, color: "#52c41a", cursor: "pointer" }} />
                                        </Tooltip>
                                    </span>
                                ),
                                rightField: <Form.Item name="new_reporting_hod" style={{ marginBottom: 0 }}><Select showSearch options={filteredHODOptions} optionFilterProp="label" allowClear style={{ width: "100%" }} placeholder="Auto-filled or select manually" /></Form.Item>,
                            },
                            {
                                leftLabel: "Work Location",
                                leftField: <Form.Item name="current_location" style={{ marginBottom: 0 }}><Input readOnly style={{ background: "#f5f5f5" }} /></Form.Item>,
                                rightLabel: "New Location",
                                rightField: (
                                    <Form.Item name="new_location" style={{ marginBottom: 0 }}>
                                        <Select
                                            allowClear
                                            placeholder="Select new work location"
                                            style={{ width: "100%" }}
                                            options={[
                                                { value: "Head Office", label: "Head Office" },
                                                { value: "Site Office", label: "Site Office" },
                                            ]}
                                        />
                                    </Form.Item>
                                ),
                            },
                        ].map((row, i) => (
                            <Row key={i} gutter={16} style={{ marginBottom: 4, padding: "4px 8px", background: i % 2 === 0 ? "#fafafa" : "#fff", borderRadius: 4, borderBottom: "1px solid #f0f0f0" }}>
                                <Col span={12}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>{row.leftLabel}</div>
                                    {row.leftField}
                                </Col>
                                <Col span={12}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: "#0e497b", marginBottom: 4 }}>{row.rightLabel}</div>
                                    {row.rightField}
                                </Col>
                            </Row>
                        ))}

                        <Divider orientation="left" style={{ color: "#0e497b", fontWeight: 600, marginTop: 16 }}>Dates & Email Notification</Divider>
                        <Row gutter={16}>
                            <Col span={12}><Form.Item name="transfer_initiation_date" label="Initiation Date" rules={[{ required: true, message: "Initiation date is required" }]}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
                            <Col span={12}><Form.Item name="effective_date_of_transfer" label="Effective Date" rules={[{ required: true, message: "Effective date is required" }]}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
                            <Col span={24}><Form.Item name="subject" label="Email Subject"><Input placeholder="e.g. Transfer Order – Employee Name" /></Form.Item></Col>
                            <Col span={12}><Form.Item name="from_mail" label="From Email"><Input placeholder="hr@company.com" /></Form.Item></Col>
                            <Col span={12}><Form.Item name="to_mail" label="To Email (Employee)"><Input placeholder="employee@company.com" /></Form.Item></Col>
                            <Col span={24}><Form.Item name="cc_mails" label="CC Emails (semicolon-separated)"><Input placeholder="manager@company.com; hr2@company.com" /></Form.Item></Col>
                            <Col span={24}>
                                <Form.Item 
                                    name="email_body" 
                                    label={
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                            <span>Email Body Content (Editable Template)</span>
                                            <Button 
                                                size="small" 
                                                type="link" 
                                                onClick={() => form.setFieldsValue({ email_body: generateDefaultEmailTemplate() })}
                                                style={{ padding: 0, height: "auto" }}
                                            >
                                                Reset / Auto-Fill Default Template
                                            </Button>
                                        </div>
                                    }
                                    tooltip="This email body content is fully editable. You can edit any sentence or click 'Reset / Auto-Fill Default Template'."
                                >
                                    <Input.TextArea rows={9} placeholder="Edit transfer email body content..." />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Spin>
            </Modal>

            {/* ── View Modal ── */}
            <Modal title={null}
                open={!!viewRecord} onCancel={() => setViewRecord(null)}
                footer={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                            {isSuperuser && viewRecord?.status === "PENDING" && (
                                <>
                                    <Button type="primary" style={{ backgroundColor: "green", borderColor: "green", marginRight: 8 }} icon={<CheckOutlined />} onClick={() => { setApproveRecord(viewRecord); setViewRecord(null); }}>Approve</Button>
                                    <Button type="primary" danger icon={<CloseOutlined />} onClick={() => { setRejectRecord(viewRecord); setViewRecord(null); }}>Reject</Button>
                                </>
                            )}
                        </div>
                        <Button onClick={() => setViewRecord(null)}>Close</Button>
                    </div>
                }
                width={860}>
                {viewRecord && (
                    <>
                        <div style={{ background: "#f0f4f8", borderRadius: 6, padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, border: "1px solid #d6e4f0" }}>
                            <div>
                                <div style={{ color: "#0e497b", fontSize: 16, fontWeight: 700 }}>{viewRecord.employee_name}</div>
                                <div style={{ color: "#555", fontSize: 12 }}>
                                    Code: {viewRecord.current_employee_code}
                                    {viewRecord.new_employee_code && viewRecord.new_employee_code !== viewRecord.current_employee_code && (
                                        <> → <strong style={{ color: "#0e497b" }}>{viewRecord.new_employee_code}</strong></>
                                    )}
                                    &nbsp;|&nbsp; Raised by: {viewRecord.created_by_name || "-"}
                                    {viewRecord.transfer_initiation_date && <>&nbsp;|&nbsp;Initiated: {dayjs(viewRecord.transfer_initiation_date).format("DD MMM YYYY")}</>}
                                    {viewRecord.effective_date_of_transfer && <>&nbsp;|&nbsp;Effective: {dayjs(viewRecord.effective_date_of_transfer).format("DD MMM YYYY")}</>}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <Tag color={STATUS_COLORS[viewRecord.status]}>{viewRecord.status}</Tag>
                                <Tag color={viewRecord.employee_acceptance_status === "ACCEPTED" ? "green" : (viewRecord.employee_acceptance_status === "DECLINED" ? "red" : "orange")}>
                                    Emp Response: {viewRecord.employee_acceptance_status || "PENDING"}
                                </Tag>
                            </div>
                        </div>

                        <Row gutter={12} style={{ marginBottom: 8 }}>
                            <Col span={6} />
                            <Col span={9}><div style={{ background: "#f5f5f5", border: "1px solid #d9d9d9", borderRadius: 4, textAlign: "center", padding: "5px 0", fontWeight: 600, color: "#555", fontSize: 13 }}>Current</div></Col>
                            <Col span={9}><div style={{ background: "#f0f4f8", border: "1px solid #d6e4f0", borderRadius: 4, textAlign: "center", padding: "5px 0", fontWeight: 600, color: "#0e497b", fontSize: 13 }}>After Transfer</div></Col>
                        </Row>

                        {[
                            { label: "Emp Code", from: viewRecord.current_employee_code, to: viewRecord.new_employee_code || viewRecord.current_employee_code },
                            { label: "Organisation", from: viewRecord.current_organisation_name || orgOptions.find(o => o.value === viewRecord.current_organisation)?.label, to: viewRecord.new_organisation_name || orgOptions.find(o => o.value === viewRecord.new_organisation)?.label },
                            { label: "Department", from: viewRecord.current_department_name || deptOptions.find(d => d.value === viewRecord.current_department)?.label, to: viewRecord.new_department_name || deptOptions.find(d => d.value === viewRecord.new_department)?.label },
                            { label: "Designation", from: viewRecord.current_designation_name || desigOptions.find(d => d.value === viewRecord.current_designation)?.label, to: viewRecord.new_designation_name || desigOptions.find(d => d.value === viewRecord.new_designation)?.label },
                            { label: "Reporting To", from: viewRecord.current_reporting_to_name || empOptions.find(e => e.value === viewRecord.current_reporting_to)?.label, to: viewRecord.new_reporting_to_name || empOptions.find(e => e.value === viewRecord.new_reporting_to)?.label },
                            { label: "HOD", from: viewRecord.current_reporting_hod_name || empOptions.find(e => e.value === viewRecord.current_reporting_hod)?.label, to: viewRecord.new_reporting_hod_name || empOptions.find(e => e.value === viewRecord.new_reporting_hod)?.label },
                            { label: "Location", from: viewRecord.current_location, to: viewRecord.new_location },
                            // Dates as a combined info row
                            { label: "Initiation", from: viewRecord.transfer_initiation_date ? dayjs(viewRecord.transfer_initiation_date).format("DD/MM/YYYY") : "—", to: viewRecord.effective_date_of_transfer ? dayjs(viewRecord.effective_date_of_transfer).format("DD/MM/YYYY") : "—", overrideLabels: { left: "Initiated", right: "Effective" } },
                        ].map((row, i) => {
                            const isHierarchy = row.label === "Reporting To" || row.label === "HOD";
                            const valueTo = row.to || (isHierarchy ? row.from : null);
                            const isDefaulted = isHierarchy && !row.to;
                            const changed = row.from !== row.to && !!row.to;

                            return (
                                <Row key={i} gutter={12} style={{ marginBottom: 4, padding: "6px 0", background: i % 2 === 0 ? "#fafafa" : "#fff", borderRadius: 4, borderBottom: "1px solid #f0f0f0" }}>
                                    <Col span={6} style={{ display: "flex", alignItems: "center", fontWeight: 600, color: "#555", fontSize: 12, paddingLeft: 8 }}>{row.label}</Col>
                                    <Col span={9}>
                                        <div style={{ background: "#f5f5f5", border: "1px solid #d9d9d9", borderRadius: 4, padding: "5px 10px", fontSize: 12, color: "#333", minHeight: 32 }}>
                                            {row.overrideLabels ? <span style={{ color: "#888", fontWeight: 600 }}>{row.overrideLabels.left}: </span> : null}
                                            {row.from || <span style={{ color: "#bbb" }}>—</span>}
                                        </div>
                                    </Col>
                                    <Col span={9}>
                                        <div style={{ 
                                            background: changed ? "#f0f4f8" : (isDefaulted ? "#fffbe6" : "#fafafa"), 
                                            border: changed ? "1px solid #0e497b" : (isDefaulted ? "1px solid #ffe58f" : "1px solid #d9d9d9"), 
                                            borderRadius: 4, padding: "5px 10px", fontSize: 12, 
                                            color: changed ? "#0e497b" : (isDefaulted ? "#856404" : "#999"), 
                                            fontWeight: (changed || isDefaulted) ? 600 : 400, 
                                            minHeight: 32 
                                        }}>
                                            {row.overrideLabels ? <span style={{ color: "#888", fontWeight: 600 }}>{row.overrideLabels.right}: </span> : null}
                                            {valueTo ? (
                                                <>
                                                    {valueTo}
                                                    {isDefaulted && <span style={{ fontSize: 10, fontStyle: "italic", marginLeft: 5 }}>(No Change)</span>}
                                                </>
                                            ) : <span style={{ color: "#bbb" }}>—</span>}
                                        </div>
                                    </Col>
                                </Row>
                            );
                        })}

                        {/* Remarks — always shown if present */}
                        {viewRecord.remarks && (
                            <div style={{ marginTop: 14, background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 4, padding: "10px 14px" }}>
                                <div style={{ fontWeight: 600, color: "#856404", fontSize: 12, marginBottom: 4 }}>Remarks (by Approver)</div>
                                <div style={{ fontSize: 13, color: "#333" }}>{viewRecord.remarks}</div>
                            </div>
                        )}

                        {/* Email Body Content */}
                        {viewRecord.email_body && (
                            <div style={{ marginTop: 10, background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 4, padding: "10px 14px" }}>
                                <div style={{ fontWeight: 600, color: "#0e497b", fontSize: 12, marginBottom: 4 }}>
                                    Email Body Content
                                </div>
                                <div style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap" }}>{viewRecord.email_body}</div>
                            </div>
                        )}

                        {/* Employee Response Remarks */}
                        {viewRecord.employee_response_remarks && (
                            <div style={{ marginTop: 10, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 4, padding: "10px 14px" }}>
                                <div style={{ fontWeight: 600, color: "#166534", fontSize: 12, marginBottom: 4 }}>
                                    Employee Response Comments ({viewRecord.employee_acceptance_status})
                                </div>
                                <div style={{ fontSize: 13, color: "#166534" }}>{viewRecord.employee_response_remarks}</div>
                            </div>
                        )}
                    </>
                )}
            </Modal>

            {/* ── Approve Modal ── */}
            <Modal title="Approve Employee Transfer" open={!!approveRecord}
                onOk={handleApprove} confirmLoading={approveLoading}
                onCancel={() => { setApproveRecord(null); setApproveRemarks(""); }}
                okText="Approve" okButtonProps={{ style: { backgroundColor: "green", borderColor: "green" } }}>
                {approveRecord && (
                    <p>Approve transfer for <strong>{approveRecord.employee_name}</strong> from <strong>{approveRecord.current_department_name}</strong> to <strong>{approveRecord.new_department_name}</strong>?
                        <br /><span style={{ color: "#faad14", fontSize: 12 }}>⚠ This will immediately update the employee record.</span>
                    </p>
                )}
                <Input.TextArea rows={3} placeholder="Remarks (optional)"
                    value={approveRemarks} onChange={e => setApproveRemarks(e.target.value)} />
            </Modal>

            {/* ── Reject Modal ── */}
            <Modal title="Reject Employee Transfer" open={!!rejectRecord}
                onOk={handleReject} confirmLoading={rejectLoading}
                onCancel={() => { setRejectRecord(null); setRejectRemarks(""); }}
                okText="Reject" okButtonProps={{ danger: true }}>
                {rejectRecord && <p>Reject transfer for <strong>{rejectRecord.employee_name}</strong>?</p>}
                <Input.TextArea rows={3} placeholder="Reason for rejection"
                    value={rejectRemarks} onChange={e => setRejectRemarks(e.target.value)} />
            </Modal>

            {/* ── Delete Modal ── */}
            <Modal title="Delete Transfer Order" open={!!deleteId}
                onOk={handleDelete} confirmLoading={deleteLoading}
                onCancel={() => setDeleteId(null)}
                okText="Delete" okButtonProps={{ danger: true }}>
                <p>Are you sure you want to delete this transfer order?</p>
            </Modal>
        </>
    );
}
